// hooks/useClickOutside.js
import { useCallback, useEffect } from "react";

/**
 * This watches clicks/touches outside of the provided references
 * If a click or touch happens outside of the provided references, onOutside() is called.
 * This function is called when the DropdownMenuPortal component mounts
 */
export default function useClickOutside(refs, onOutside) {
  /**
   * .some() iterates over arrays.
   * For each element, it executes a callback function (passed in as an arg)
   * If the callback returns a truthy value for any of the element, then .some() returns a 'true' and stops iterating.
   * If the callback returns a false value for ALL the values in the array, then .some returns 'false'
   * Since the event we are using is "mousedown", it covers both left and right clicks, so we igore right clicks (e.button === 2)
   */
  const handle = useCallback(
    (e) => {
      if (e.button === 2) return; // ignore right-click
      const inside = refs.some(
        (r) => r.current && r.current.contains(e.target)
      );
      if (!inside) onOutside?.();
    },
    [refs, onOutside]
  );

  /**
   * We use useEffect for 2 reasons:
   *    1. To make sure that we only reference refs that exist.
   *        useEffect only runs after React commits the DOM -> All refs are pointing to DOM elements that exist.
   *    2. To have a reliable way to clean up after every mount
   *        On all DropdownMenuPortal component unmounts, the event listeners are removed.
   *        This is great for performance and memory because if we do not clean up,
   *        then over time, we would accumulate more and more event listeners on the same refs
   * useEffect run when its dependencies change, so if refs or onOutside change, it will clean up and then run again.
   */
  useEffect(() => {
    document.addEventListener("pointerdown", handle, { passive: true });
    return () => document.removeEventListener("pointerdown", handle);
  }, [handle]);
}
